import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Archive, 
  Search, 
  RotateCcw, 
  Eye, 
  Loader2,
  FileText,
  AlertCircle,
  CheckCircle2,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ArchivedPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [unarchiving, setUnarchiving] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);

  useEffect(() => {
    fetchArchivedApplications();
  }, []);

  const fetchArchivedApplications = async () => {
    setLoading(true);
    try {
      const response = await api().get('/applications/archived');
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Failed to fetch archived applications:', error);
      toast.error('Failed to load archived applications');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (appId) => {
    setUnarchiving(appId);
    try {
      await api().post(`/applications/${appId}/unarchive`);
      toast.success('Application restored to active');
      setSelectedApps(prev => prev.filter(id => id !== appId));
      fetchArchivedApplications();
    } catch (error) {
      toast.error('Failed to restore application');
    } finally {
      setUnarchiving(null);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedApps(filteredApplications.map(a => a.id));
    } else {
      setSelectedApps([]);
    }
  };

  const handleSelectApp = (appId, checked) => {
    if (checked) {
      setSelectedApps(prev => [...prev, appId]);
    } else {
      setSelectedApps(prev => prev.filter(id => id !== appId));
    }
  };

  const handleBulkUnarchive = async () => {
    if (!window.confirm(`Are you sure you want to restore ${selectedApps.length} application(s)?`)) return;
    try {
      await Promise.all(selectedApps.map(id => api().post(`/applications/${id}/unarchive`)));
      toast.success(`${selectedApps.length} application(s) restored`);
      setSelectedApps([]);
      fetchArchivedApplications();
    } catch (error) {
      toast.error('Failed to restore some applications');
    }
  };

  const filteredApplications = applications.filter(app =>
    app.student_name?.toLowerCase().includes(search.toLowerCase()) ||
    app.student_email?.toLowerCase().includes(search.toLowerCase()) ||
    app.id?.toLowerCase().includes(search.toLowerCase())
  );

  const getDocStatus = (app) => {
    const required = app.total_required_docs || 0;
    const uploaded = app.uploaded_docs_count || 0;
    return { required, uploaded, complete: uploaded >= required };
  };

  return (
    <div className="space-y-6" data-testid="archived-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Archive className="w-6 h-6" />
            Archived Applications
          </h1>
          <p className="text-muted-foreground">
            Applications archived due to incomplete documents after maximum reminders sent
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Auto-Archive Criteria</p>
              <p className="text-sm text-amber-700">
                Applications are automatically moved here when maximum reminders have been sent but required documents are still incomplete.
                If documents are later completed (manually or by the student), the application will automatically move back to Active.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="archive-search"
          />
        </div>
        <Badge variant="secondary">{filteredApplications.length} archived</Badge>
      </div>

      {/* Applications Table */}
      <Card>
        <CardHeader>
          <CardTitle>Archived Applications</CardTitle>
          <CardDescription>
            Click "Restore" to move an application back to active status
          </CardDescription>
        </CardHeader>
        {/* Bulk Actions Bar */}
        {selectedApps.length > 0 && (
          <div className="flex items-center justify-between px-6 py-3 bg-muted/50 border-b">
            <span className="text-sm font-medium">
              {selectedApps.length} application{selectedApps.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setSelectedApps([])}>
                Clear Selection
              </Button>
              <Button variant="default" size="sm" onClick={handleBulkUnarchive} data-testid="bulk-restore-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore Selected
              </Button>
            </div>
          </div>
        )}
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading archived applications...</span>
            </div>
          ) : filteredApplications.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No archived applications</p>
              <p className="text-sm">Applications with incomplete documents after max reminders will appear here</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox 
                        checked={selectedApps.length === filteredApplications.length && filteredApplications.length > 0}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-archived-checkbox"
                      />
                    </TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Documents</TableHead>
                    <TableHead>Reminders Sent</TableHead>
                    <TableHead>Archived Date</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredApplications.map((app) => {
                    const docStatus = getDocStatus(app);
                    return (
                      <TableRow 
                        key={app.id} 
                        data-testid={`archived-app-${app.id}`}
                        className={selectedApps.includes(app.id) ? 'bg-primary/5' : ''}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedApps.includes(app.id)}
                            onCheckedChange={(checked) => handleSelectApp(app.id, checked)}
                            data-testid={`select-archived-${app.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{app.student_name || 'N/A'}</TableCell>
                        <TableCell>{app.student_email || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {docStatus.complete ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            )}
                            <span className={docStatus.complete ? 'text-green-600' : 'text-amber-600'}>
                              {docStatus.uploaded}/{docStatus.required}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{app.reminders_sent || 0}</Badge>
                        </TableCell>
                        <TableCell>
                          {app.archived_at ? new Date(app.archived_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {app.archive_reason || 'Max reminders reached'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/applications/${app.id}`)}
                              data-testid={`view-archived-${app.id}`}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleUnarchive(app.id)}
                              disabled={unarchiving === app.id}
                              data-testid={`unarchive-${app.id}`}
                            >
                              {unarchiving === app.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4 mr-1" />
                              )}
                              Restore
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ArchivedPage;
